import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
/**
 * SEO component for adding metadata to pages
 * @param {Object} props - Component props
 * @param {Object} props.meta - Metadata for the page
 * @param {string} props.meta.title - Page title
 * @param {string} props.meta.description - Page description
 * @param {string} props.meta.canonical - Canonical URL
 * @param {string} props.meta.ogImage - Open Graph image URL
 * @param {Object} props.structuredData - Schema.org structured data
 */
const SEO = ({ meta, structuredData }) => {
  // Dynamically detect the current URL
  const getCurrentUrl = () => {
    return window.location.origin;
  };

  // Check if we're on staging environment
  const isStaging = () => {
    const hostname = window.location.hostname;
    return hostname.includes('staging');
  };

  const defaultMeta = {
    title: 'EladoreRuffles | Beautiful Hair Accessories for Kids',
    description: 'Shop our collection of handmade hair bows, headbands and accessories for kids. Beautiful designs for every occasion.',
    canonical: getCurrentUrl(),
    // A product, not the logo: a logo on white reads as a blank card when a page
    // is shared. Server-rendered tags in backend/utils/ogTags.js are what
    // crawlers actually read; this is the in-app fallback.
    ogImage: `${getCurrentUrl()}/og-default.jpg`,
  };
  const metaData = { ...defaultMeta, ...meta };

  return (
    <Helmet>
      {/* Block indexing on staging environment */}
      {isStaging() && (
        <>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </>
      )}

      {/* Basic metadata */}
      <title>{metaData.title}</title>
      <meta name="description" content={metaData.description} />
      {/* Canonical URL */}
      <link rel="canonical" href={metaData.canonical} />
      {/* Open Graph tags */}
      <meta property="og:title" content={metaData.title} />
      <meta property="og:description" content={metaData.description} />
      <meta property="og:url" content={metaData.canonical} />
      <meta property="og:image" content={metaData.ogImage} />
      <meta property="og:type" content="website" />
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaData.title} />
      <meta name="twitter:description" content={metaData.description} />
      <meta name="twitter:image" content={metaData.ogImage} />
      {/* Schema.org structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
export default SEO;
